import { z } from "zod";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "./pagination";

/**
 * M7c NOC stages, shown to people as SECTORS (Game Settings › Sector
 * Management). Migration 0039 (user decisions 2026-10-19); 0040 made the
 * stages the sidebar's four sectors (user, 2026-10-21). The organising
 * committee opens the GMS to NOCs step by step:
 *
 *   Sector 1  ENTRIES        Early Planning Phase: the dashboard, EOI & EBN.
 *   Sector 2  ACCREDITATION  + Accreditation: registration (every category),
 *                            Shortlist, E-Accreditation.
 *   Sector 3  LOGISTICS      + Logistics & Hospitality: Arrival & Departure,
 *                            Transport, Accommodation, F&B.
 *   Sector 4  CONTINGENT     + the Contingent Dashboard (GMS only; it reads
 *                            the other modules' summaries), the Incident
 *                            Helpdesk (also Accommodation's helpdesk tab).
 *
 * Re-ordered by the user 2026-10-29 (migration 0063): the dashboard moved to
 * Sector 1, Shortlist / E-Accreditation to Sector 2, logistics to Sector 3.
 *
 * Stages are CUMULATIVE ("in order", user 2026-10-21). Only the ORGANIZATION role is staged: admins,
 * OPERATION and facility roles never are. One current stage for every NOC
 * (`noc_stage_settings`), plus per-organisation exceptions ahead or behind
 * (`noc_stage_overrides`). A login scoped to several organisations gets the
 * LOWEST effective stage among them (fail closed).
 *
 * Enforced by the SERVER (403 STAGE_LOCKED, details `{ module,
 * opens_at_stage, stage }`); the GMS reads `/auth/me.noc_stage` and never
 * re-derives it. Adding a stage = one entry here + a migration widening the
 * CHECKs (latest: 0040; 0063 re-ordered the modules).
 */

export const NOC_MODULES = ["entries", "registration", "travel", "transport", "accommodation", "fnb", "shortlist", "e_accreditation", "helpdesk", "dashboard", "contingent"] as const;
export type NocModule = (typeof NOC_MODULES)[number];

export const NOC_STAGES = [
	{ stage: 1, key: "ENTRIES", modules: ["entries", "dashboard"] },
	{ stage: 2, key: "ACCREDITATION", modules: ["registration", "shortlist", "e_accreditation"] },
	{ stage: 3, key: "LOGISTICS", modules: ["travel", "transport", "accommodation", "fnb"] },
	{ stage: 4, key: "CONTINGENT", modules: ["contingent", "helpdesk"] },
] as const satisfies ReadonlyArray<{ stage: number; key: string; modules: readonly NocModule[] }>;

export type NocStageKey = (typeof NOC_STAGES)[number]["key"];
export const NOC_STAGE_MIN = 1;
/** The last stage (CHECK 1..NOC_STAGE_MAX, 0040). */
export const NOC_STAGE_MAX = NOC_STAGES.length;
/** The audit headline of a stage write. */
export const NOC_STAGE_REASON_MAX = 500;
/** `GET /noc-stages`: default page size (there are 11 NOCs). */
export const NOC_STAGE_PAGE_SIZE_DEFAULT = 50;

/** The stage a module first opens at. Every module is in exactly one stage. */
export function nocModuleOpensAt(module: NocModule): number {
	for (const s of NOC_STAGES) if ((s.modules as readonly NocModule[]).includes(module)) return s.stage;
	return NOC_STAGE_MAX;
}

/** Whether `module` is open at `stage` (cumulative). */
export const nocModuleOpen = (stage: number, module: NocModule): boolean => nocModuleOpensAt(module) <= stage;

/** Every module open at `stage`, in NOC_MODULES order. */
export const nocOpenModules = (stage: number): NocModule[] => NOC_MODULES.filter((m) => nocModuleOpen(stage, m));

/** The stage descriptor (key, modules opened by it). */
export const nocStageOf = (stage: number) => NOC_STAGES.find((s) => s.stage === stage) ?? null;

// ── Bodies ──────────────────────────────────────────────────────────────────

const stageNumber = z.number().int().min(NOC_STAGE_MIN).max(NOC_STAGE_MAX);
const reason = z.string().trim().min(1).max(NOC_STAGE_REASON_MAX);

/** `PUT /noc-stages/current` (SUPER_ADMIN) — move every NOC that follows the current stage. */
export const putCurrentStageBody = z.object({ stage: stageNumber, reason });
export type PutCurrentStageBody = z.input<typeof putCurrentStageBody>;

/** `PUT /noc-stages/organizations/:id` (SUPER_ADMIN) — `stage: null` = follow the current stage again. */
export const putOrganizationStageBody = z.object({ stage: stageNumber.nullable(), reason });
export type PutOrganizationStageBody = z.input<typeof putOrganizationStageBody>;

/** `GET /noc-stages` (ADMIN, SUPER_ADMIN) — NOC organisations, offset paged. */
export const nocStagesQuery = paginationQuery.extend({
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(NOC_STAGE_PAGE_SIZE_DEFAULT),
	search: searchParam.optional(),
});
export type NocStagesQuery = z.infer<typeof nocStagesQuery>;
export type NocStagesQueryInput = z.input<typeof nocStagesQuery>;

// ── Responses ───────────────────────────────────────────────────────────────

export interface NocStageOrganization {
	organization: { id: string; name: string; code: string | null };
	/** null = follows the current stage. */
	override_stage: number | null;
	effective_stage: number;
}

/** `GET /noc-stages`. `organizations` is one page; `total` counts them all. */
export interface NocStageOverview {
	current_stage: number;
	updated_at: string;
	organizations: NocStageOrganization[];
	page: number;
	page_size: number;
	total: number;
	total_pages: number;
}

/** `PUT /noc-stages/current`. */
export interface NocCurrentStage {
	current_stage: number;
	updated_at: string;
}

/** `/auth/me.noc_stage` — null for every session whose active role is not ORGANIZATION. */
export interface MeNocStage {
	stage: number;
	open_modules: NocModule[];
}

/** 403 STAGE_LOCKED `details`. */
export interface StageLockedDetails {
	module: NocModule;
	opens_at_stage: number;
	stage: number;
}
